import { type FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import { coverFor, fmtDuration, slugify } from "../../lib/helpers";
import type { CourseListItem } from "../../lib/types";

const AUDIENCE_META: Record<string, { cls: string; icon: string; label: string }> = {
  medic: { cls: "aud-medic", icon: "fa-user-doctor", label: "Medici" },
  rezident: { cls: "aud-rezident", icon: "fa-stethoscope", label: "Rezidenți" },
  asistent: { cls: "aud-asistent", icon: "fa-hand-holding-medical", label: "Asistenți" },
};

const AUD_PILL_CSS = `
.audience-pill { display:inline-flex; align-items:center; gap:6px; padding:3px 9px; border-radius:999px; font-size:.72rem; font-weight:700; margin-right:4px; }
.aud-medic { background:var(--c-primary-light); color:var(--c-primary-dark); }
.aud-rezident { background:#fdf1de; color:#8a5a04; }
.aud-asistent { background:#ede4fa; color:#4a2c91; }
`;

export function AdminCourses() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const { data: all = [] } = useQuery({ queryKey: ["courses"], queryFn: () => request<CourseListItem[]>("/courses") });
  const list = useMemo(
    () => filter ? all.filter((c) => (c.audiences || []).includes(filter)) : all,
    [all, filter]
  );

  const create = useMutation({
    mutationFn: (body: object) => request<CourseListItem>("/courses", { method: "POST", body }),
    onSuccess: (c) => {
      toast("Curs creat", "success");
      qc.invalidateQueries({ queryKey: ["courses"] });
      setOpenModal(false);
      navigate(`/admin/curs-editor/${c.id}`);
    },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  function statusPill(s: string) {
    if (s === "published") return <span className="pill success">Publicat</span>;
    if (s === "draft") return <span className="pill warning">Schiță</span>;
    return <span className="pill neutral">Arhivat</span>;
  }

  return (
    <>
      <style>{AUD_PILL_CSS}</style>
      <AppBar crumbs={[{ label: "Tablou de bord", to: "/admin/dashboard" }, { label: "Cursuri" }]} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Cursuri</h1>
            <p>Catalog organizat pe categorii de utilizatori — fiecare student vede doar cursurile rolului său.</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-primary" onClick={() => setOpenModal(true)}><i className="fa-solid fa-plus"></i> Curs nou</button>
          </div>
        </div>

        <div className="card">
          <div className="tabs">
            <div className={"tab" + (filter === null ? " active" : "")} onClick={() => setFilter(null)}>Toate</div>
            {Object.entries(AUDIENCE_META).map(([key, m]) => (
              <div key={key} className={"tab" + (filter === key ? " active" : "")} onClick={() => setFilter(key)}>
                <i className={`fa-solid ${m.icon}`}></i> {m.label}
              </div>
            ))}
          </div>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Curs</th><th>Public țintă</th><th>Categorie</th><th>Lecții</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr><td colSpan={6} className="text-muted">Niciun curs.</td></tr>
                ) : list.map((c) => (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/curs-editor/${c.id}`)}>
                    <td>
                      <div className="user-cell">
                        <div style={{ width: 42, height: 42, borderRadius: 10, background: coverFor(c) }}></div>
                        <div><strong>{c.title}</strong><br /><small className="muted">{c.instructor?.full_name || "—"}</small></div>
                      </div>
                    </td>
                    <td>
                      {(c.audiences || []).map((a) => {
                        const m = AUDIENCE_META[a];
                        if (!m) return null;
                        return <span key={a} className={"audience-pill " + m.cls}><i className={`fa-solid ${m.icon}`}></i> {m.label}</span>;
                      })}
                    </td>
                    <td>{c.category}</td>
                    <td>{fmtDuration(c.duration_minutes)}</td>
                    <td>{statusPill(c.status)}</td>
                    <td className="text-right">
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/curs-editor/${c.id}`); }}>Editează</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <NewCourseModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSubmit={(body) => create.mutate(body)}
        />
      </div>
    </>
  );
}

function NewCourseModal({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (body: object) => void }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [category, setCategory] = useState("Neonatologie");
  const [level, setLevel] = useState("intermediate");
  const [auds, setAuds] = useState({ medic: false, rezident: true, asistent: false });
  const toast = useToast();

  function submit(e: FormEvent) {
    e.preventDefault();
    const audiences = Object.entries(auds).filter(([_, on]) => on).map(([k]) => k);
    if (audiences.length === 0) { toast("Selectează cel puțin un public țintă", "error"); return; }
    onSubmit({
      title, slug, subtitle: subtitle || null, category, level, audiences,
    });
  }

  function onTitleChange(v: string) {
    setTitle(v);
    if (!slugManual) setSlug(slugify(v));
  }

  return (
    <Modal open={open} onClose={onClose} title="Curs nou" maxWidth={600}>
      <form onSubmit={submit}>
        <div className="form-group"><label>Titlu</label><input className="form-control" required value={title} onChange={(e) => onTitleChange(e.target.value)} /></div>
        <div className="form-group"><label>Slug (URL)</label><input className="form-control" required value={slug} onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }} /></div>
        <div className="form-group"><label>Subtitlu</label><input className="form-control" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} /></div>
        <div className="grid-2">
          <div className="form-group"><label>Categorie</label><input className="form-control" required value={category} onChange={(e) => setCategory(e.target.value)} /></div>
          <div className="form-group">
            <label>Nivel</label>
            <select className="form-control" value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="beginner">Începător</option>
              <option value="intermediate">Intermediar</option>
              <option value="advanced">Avansat</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Public țintă</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {(["medic", "rezident", "asistent"] as const).map((k) => {
              const m = AUDIENCE_META[k];
              return (
                <label key={k} className={"form-check audience-pill " + m.cls}>
                  <input type="checkbox" checked={auds[k]} onChange={(e) => setAuds({ ...auds, [k]: e.target.checked })} />
                  {" "}<i className={`fa-solid ${m.icon}`}></i> {m.label}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex flex-between mt-24">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Anulează</button>
          <button type="submit" className="btn btn-primary">Creează & editează →</button>
        </div>
      </form>
    </Modal>
  );
}
